import { CheckCircle2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { UploadError } from '@nucleus-commerce/admin-kit/uploads';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Progress } from '@nucleus-commerce/ui/components/progress';

import type { MediaUploadItem } from '../hooks/use-media-uploader';

import { formatBytes } from './format-bytes';

export interface UploadQueueListProps {
  items: readonly MediaUploadItem[];
  onCancel: (id: string) => void;
  onRemove: (id: string) => void;
  onClearFinished: () => void;
}

/** In-flight and recently finished uploads with progress; disappears once cleared. */
export function UploadQueueList({
  items,
  onCancel,
  onRemove,
  onClearFinished,
}: UploadQueueListProps) {
  const { t } = useTranslation(['media', 'common']);
  const message = useErrorMessage();
  if (items.length === 0) return null;
  const finished = items.some((i) => i.status !== 'queued' && i.status !== 'uploading');

  const describe = (item: MediaUploadItem) => {
    if (item.status === 'error') {
      return item.error instanceof UploadError
        ? t(`media:upload.errors.${item.error.code}`)
        : message(item.error);
    }
    return t(`media:upload.status.${item.status}`);
  };

  return (
    <div className="bg-card rounded-xl border" aria-live="polite">
      <div className="flex items-center justify-between border-b px-4 py-2">
        <span className="text-sm font-medium">
          {t('media:upload.queue', { count: items.length })}
        </span>
        {finished ? (
          <Button variant="ghost" size="xs" onClick={onClearFinished}>
            {t('media:upload.clearFinished')}
          </Button>
        ) : null}
      </div>
      <ul className="divide-y">
        {items.map((item) => (
          <li key={item.id} className="flex items-center gap-3 px-4 py-2 text-sm">
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate">{item.file.name}</span>
                <span className="text-muted-foreground shrink-0 text-xs">
                  {formatBytes(item.file.size)}
                </span>
              </div>
              {item.status === 'uploading' || item.status === 'queued' ? (
                <Progress value={Math.round(item.progress.ratio * 100)} className="mt-1.5 h-1" />
              ) : (
                <span
                  className={
                    item.status === 'error'
                      ? 'text-destructive text-xs'
                      : 'text-muted-foreground text-xs'
                  }
                >
                  {describe(item)}
                </span>
              )}
            </div>
            {item.status === 'done' ? (
              <CheckCircle2 className="text-success size-4 shrink-0" aria-hidden />
            ) : (
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={
                  item.status === 'uploading' || item.status === 'queued'
                    ? t('common:actions.cancel')
                    : t('common:actions.close')
                }
                onClick={() =>
                  item.status === 'uploading' || item.status === 'queued'
                    ? onCancel(item.id)
                    : onRemove(item.id)
                }
              >
                <X aria-hidden />
              </Button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
